import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import AnalyticsCard from './components/AnalyticsCard';
import SearchInterface from './components/SearchInterface';
import SearchResults from './components/SearchResults';
import RecentArtists from './components/RecentArtists';
import PopularArtists from './components/PopularArtists';
import QuickActions from './components/QuickActions';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';

const EditorDashboard = () => {
  const { user, userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [analyticsData, setAnalyticsData] = useState([
    { title: "Total Artists", value: "—", change: "", changeType: "positive", icon: "Users", color: "primary" },
    { title: "Monthly Visitors", value: "—", change: "", changeType: "positive", icon: "Eye", color: "success" },
    { title: "Content Reviews", value: "—", change: "", changeType: "positive", icon: "FileText", color: "accent" },
    { title: "Pending Approvals", value: "—", change: "", changeType: "negative", icon: "Clock", color: "warning" }
  ]);
  const [recentArtists, setRecentArtists] = useState([]);
  const [popularArtists, setPopularArtists] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoadingData(true);
        const result = await userService?.getUsers({
          role: 'artist',
          pageSize: 50,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });

        const artists = result?.users || [];
        const totalArtists = result?.totalCount || artists?.length;

        // Build analytics from real data
        setAnalyticsData([
          {
            title: "Total Artists",
            value: totalArtists?.toString(),
            change: "",
            changeType: "positive",
            icon: "Users",
            color: "primary"
          },
          {
            title: "Active Artists",
            value: artists?.filter(a => a?.status === 'active')?.length?.toString(),
            change: "",
            changeType: "positive",
            icon: "Eye",
            color: "success"
          },
          {
            title: "Content Reviews",
            value: artists?.filter(a => a?.status === 'pending')?.length?.toString(),
            change: "",
            changeType: "positive",
            icon: "FileText",
            color: "accent"
          },
          {
            title: "Pending Approvals",
            value: artists?.filter(a => a?.status === 'inactive')?.length?.toString(),
            change: "",
            changeType: "negative",
            icon: "Clock",
            color: "warning"
          }
        ]);

        // Recent artists - last 4 by created_at
        const recent = artists?.slice(0, 4)?.map(a => ({
          id: a?.id,
          name: a?.full_name || 'Unknown Artist',
          type: a?.artist_type ? (a?.artist_type?.charAt(0)?.toUpperCase() + a?.artist_type?.slice(1)) : 'Artist',
          avatar: a?.profile_image_url || null,
          lastViewed: a?.updated_at ? new Date(a?.updated_at)?.toLocaleDateString() : 'Recently',
          hasUpdates: a?.status === 'pending'
        }));
        setRecentArtists(recent);

        // Popular artists - sorted by some metric
        const popular = [...artists]?.slice(0, 5)?.map((a, idx) => ({
          id: a?.id,
          name: a?.full_name || 'Unknown Artist',
          avatar: a?.profile_image_url || null,
          views: '—',
          change: '',
          trend: 'up',
          score: (100 - idx * 5)?.toFixed(1)
        }));
        setPopularArtists(popular);

      } catch (error) {
        console.error('Error loading editor dashboard data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const handleSearch = async (query) => {
    if (!query?.trim()) {
      setSearchResults([]);
      setSearchQuery('');
      return;
    }

    setIsSearching(true);
    setSearchQuery(query);
    
    try {
      const result = await userService?.getUsers({
        role: 'artist',
        search: query,
        pageSize: 20
      });

      const mapped = (result?.users || [])?.map(a => ({
        id: a?.id,
        name: a?.full_name || 'Unknown Artist',
        handle: a?.email?.split('@')?.[0] || '',
        type: a?.artist_type ? (a?.artist_type?.charAt(0)?.toUpperCase() + a?.artist_type?.slice(1)) : 'Artist',
        avatar: a?.profile_image_url || null,
        bio: a?.bio || '',
        followers: '—',
        lastActive: a?.updated_at ? new Date(a?.updated_at)?.toLocaleDateString() : 'Recently',
        status: a?.status || 'active',
        isVerified: a?.status === 'active'
      }));

      setSearchResults(mapped);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole={userProfile?.role || 'editor'} userName={userProfile?.full_name || ''} userEmail={userProfile?.email || user?.email || ''} />
      <Sidebar 
        userRole={userProfile?.role || 'editor'} 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={toggleSidebar}
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        <div className="p-6 max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Editor Dashboard
            </h1>
            <p className="text-text-secondary">
              Manage artists, review content, and monitor platform analytics
            </p>
          </div>

          {/* Analytics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {analyticsData?.map((data, index) => (
              <AnalyticsCard
                key={index}
                title={data?.title}
                value={loadingData ? '...' : data?.value}
                change={data?.change}
                changeType={data?.changeType}
                icon={data?.icon}
                color={data?.color}
              />
            ))}
          </div>

          {/* Search Interface */}
          <div className="mb-8">
            <SearchInterface 
              onSearch={handleSearch}
              isSearching={isSearching}
            />
          </div>

          {/* Search Results */}
          {(searchQuery || isSearching) && (
            <div className="mb-8">
              <SearchResults 
                results={searchResults}
                query={searchQuery}
                isLoading={isSearching}
              />
            </div>
          )}

          {/* Secondary Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Artists */}
            <div>
              <RecentArtists artists={recentArtists} />
            </div>

            {/* Popular Artists */}
            <div>
              <PopularArtists artists={popularArtists} />
            </div>

            {/* Quick Actions */}
            <div>
              <QuickActions />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditorDashboard;